import { newUuidV7, type NetworkSchematic } from '@ghp/domain'
import { Button, EmptyState, IconButton, Page, PageBody, PageHeader, TextLink } from '@ghp/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Link,
  createRoute,
  stripSearchParams,
  type SearchSchemaInput,
} from '@tanstack/react-router'
import { ArrowLeft, ChevronRight, ClipboardList } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { shellRoute, type FieldContext } from '../../../app/routes/root'
import { useTeamData } from '../../../app/use-team-data'
import { readServerRules } from '../../../offline/server-rules'
import {
  LockedDraftError,
  MissingRowError,
  updateDraft,
  updateRow,
  type CachedInquiry,
  type DataDatabase,
  type InspectionDraft,
  type LocalMedium,
  type OutboxState,
} from '../../../offline/data-db'
import {
  createDraft,
  enqueueSubmission,
  outboxEntryOf,
  rearmSubmission,
  recordInspectionStep,
  reopenReturned,
  retrySubmission,
} from '../../../offline/outbox'
import { readDevicePreferences } from '../../../offline/preferences'
import { pullInquiries, pullVocabulary } from '../../../offline/pull'
import {
  DRAFTS_QUERY_KEY,
  SERVER_RULES_QUERY_KEY,
  INQUIRIES_QUERY_KEY,
  MEDIA_QUERY_KEY,
  OUTBOX_QUERY_KEY,
  VOCABULARY_QUERY_KEY,
} from '../../../offline/query-keys'
import type { AttachmentType } from '../components/AttachmentsTab'
import { InspectionEditorScreen } from '../components/InspectionEditorScreen'
import { pullPresentationOf, type PullPresentation } from '../labels'
import { useContactsCorrection } from '../use-contacts-correction'
import { schematicFileName, schematicFingerprint } from '@ghp/schematic'
import {
  LIST_SCOPE_DEFAULTS,
  tourStateOf,
  validateListScopeSearch,
  type ListScopeSearch,
} from '../lib/list-filters'
import { followStoredLock } from '../lib/locked-drafts'
import { usePassageNoticedAt } from '../lib/passage-notices'

/**
 * « Éditeur de contrôle » — onglets Infos · Contrôle · Pièces jointes · Transmettre.
 *
 * ⚠️ Le paramètre de route est l'identifiant de la **demande** (`Inquiry.id`), pas celui d'un
 * `Inspection` : côté terrain, la fiche n'existe qu'en brouillon local, indexé par
 * `inquiryId` (D4). C'est le back-office qui créera l'`Inspection` à la validation.
 */
export const inspectionEditorRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/inspections/$inquiryId',
  // La vue d'où l'on vient, transportée pour que « Retour » y ramène. L'éditeur n'en fait rien
  // d'autre — il ne la lit que pour la rendre.
  // ⚠️ `SearchSchemaInput` sépare ce qu'on **accepte** en entrée de ce qu'on **rend** : sans lui,
  // TanStack déduit l'entrée du retour et exige `search` sur chaque `<Link to>` de l'app, jusque
  // sur la redirection de connexion. Une vue absente vaut « À faire ».
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput): ListScopeSearch =>
    validateListScopeSearch(search),
  search: {
    middlewares: [stripSearchParams<ListScopeSearch>(LIST_SCOPE_DEFAULTS)],
  },
  component: InspectionEditor,
})

/**
 * Ouvrir — ou rouvrir — la saisie d'un contrôle, **sans jamais l'inventer**.
 *
 * Une saisie déjà commencée passe d'abord et sans condition (M21) : la demande peut avoir
 * quitté le cache, le relevé de l'agent, lui, n'existe que là.
 *
 * Vient ensuite la garde — `createDraft` la porte lui-même, c'est son invariant. Elle est
 * répétée ici pour une seule raison : rendre `null` — « rien n'a été créé », un état d'écran —
 * là où `createDraft` lancerait une erreur, indistinguable d'une base illisible.
 *
 * ⚠️ **L'ouverture passe par `createDraft` dans les deux cas**, y compris sur une fiche déjà
 * commencée : c'est lui qui porte l'invariant, et il est **idempotent** (#47) — une seconde
 * ouverture rend le même brouillon, donc la même clé d'idempotence, celle qui rend le rejeu
 * d'une transmission inoffensif.
 */
async function openDraft(db: DataDatabase, inquiryId: string): Promise<InspectionDraft | null> {
  const started = await db.drafts.get(inquiryId)

  if (started === undefined) {
    // Lu dans la base de **l'équipe connectée** : sur une demande confiée à une autre équipe,
    // l'écran répond « Contrôle inconnu » — le même écran que sur une demande absente, avec la
    // même issue.
    const inquiry = await db.inquiries.get(inquiryId)
    if (inquiry === undefined) return null
  }

  return createDraft(db, inquiryId)
}

/**
 * Ce que la reprise depuis « Contrôle inconnu » dit de son résultat. `done` ne rouvre pas la
 * fiche de lui-même — si la demande est descendue, l'invalidation des clés a déjà fait
 * basculer l'écran vers l'éditeur ; lire ce message, c'est que la tournée à jour ne porte
 * vraiment pas ce contrôle.
 */
const REDOWNLOAD_MESSAGE: Record<PullPresentation, string> = {
  done: "La tournée est à jour, et ce contrôle n'y figure pas : prévenez le bureau.",
  refused:
    "Le serveur a refusé l'accès à la tournée avec ce compte. Reconnectez-vous avec un compte d'agent de terrain, et prévenez le bureau si le refus persiste.",
  unreachable:
    "Pas de réseau pour le moment : la tournée redescendra d'elle-même dès que la tablette en retrouvera.",
  faulty:
    "La tournée n'a pas pu être téléchargée, et ce n'est pas une affaire de réseau : prévenez le bureau.",
}

/**
 * Conteneur de route : Dexie → écran. **Aucune lecture réseau pour afficher** — la source de
 * vérité est IndexedDB (D21), et une URL profonde rechargée dans un vide sanitaire doit
 * rendre la même fiche qu'en surface.
 */
function InspectionEditor() {
  const { inquiryId } = inspectionEditorRoute.useParams()
  const { view } = inspectionEditorRoute.useSearch()
  const context = inspectionEditorRoute.useRouteContext()
  const { shared, sync, accessToken, auth } = context
  const data = useTeamData()
  const queryClient = useQueryClient()

  const inquiry = useQuery({
    queryKey: [...INQUIRIES_QUERY_KEY, inquiryId],
    queryFn: async () => (await data.inquiries.get(inquiryId)) ?? null,
  })

  /**
   * L'issue de « Contrôle inconnu » (M21) : redescendre la tournée **d'ici**, sans renvoyer
   * l'agent naviguer en espérant. Si la demande arrive, l'invalidation des clés relit
   * `openDraft` et l'écran bascule tout seul vers l'éditeur ; sinon, le motif classé dit quoi
   * faire — les mots sont ceux de « Mes contrôles » devant le même échec.
   */
  const [redownload, setRedownload] = useState<'idle' | 'running' | PullPresentation>('idle')

  const redownloadTour = useCallback(() => {
    setRedownload('running')
    // Le `401` remonte à la session comme pour les deux autres descentes de l'écran : ce geste
    // est celui d'un agent qui attend sa demande, et le laisser muet ferait patienter jusqu'au
    // tour de planificateur suivant pour une session qu'un refresh répare tout de suite.
    pullInquiries(data, shared, sync, accessToken(), (token) => auth.tokenRefused(token)).then(
      (result) => {
        if (result.outcome !== 'unavailable') {
          void queryClient.invalidateQueries({ queryKey: INQUIRIES_QUERY_KEY })
          void queryClient.invalidateQueries({ queryKey: DRAFTS_QUERY_KEY })
        }
        setRedownload(pullPresentationOf(result))
      },
      (failure: unknown) => {
        console.error('[terrain] Descente de la tournée impossible', failure)
        setRedownload('faulty')
      },
    )
  }, [data, shared, sync, accessToken, auth, queryClient])

  /**
   * Ouvrir la fiche, c'est la créer si elle n'existe pas — mais seulement pour une demande
   * que la tablette connaît (`openDraft`). `null` veut dire « rien n'a été créé ».
   */
  const draft = useQuery({
    queryKey: [...DRAFTS_QUERY_KEY, inquiryId],
    queryFn: () => openDraft(data, inquiryId),
  })

  // ⚠️ `search` est **explicite** : sans lui, le retour d'un contrôle ouvert depuis « Transmis »
  // atterrirait sur « À faire », et la ligne que l'agent vient de quitter ne serait nulle part
  // sur l'écran d'arrivée.
  const back = (
    <IconButton label="Retour aux contrôles" render={<Link to="/" search={{ view }} />}>
      <ArrowLeft size={18} aria-hidden />
    </IconButton>
  )

  /**
   * Où va l'agent une fois le contrôle parti. Le prototype l'y conduit d'autorité
   * (`go('historique')`) ; ici le geste reste le sien — l'accusé de transmission et l'état
   * de la file sont sur la fiche, et les lui retirer sous les yeux serait lui cacher le seul
   * endroit qui dit où en est son envoi.
   */
  const history = (
    <TextLink render={<Link to="/history" />}>
      Voir le contrôle dans l'historique
      <ChevronRight size={16} aria-hidden />
    </TextLink>
  )

  // Une lecture Dexie en échec (`retry: false`) laisserait « Ouverture de la fiche… » à
  // l'écran pour toujours : l'attente indéfinie est le seul message qu'on ne doit jamais rendre.
  if (draft.isError) {
    return (
      <Page>
        <PageHeader back={back} title="Fiche impossible à ouvrir" />
        <PageBody>
          <EmptyState title="La base locale n'a pas pu être lue">
            Fermez et rouvrez l'application ; si le problème persiste, prévenez le bureau — ne
            ressaisissez rien sur une autre fiche en attendant.
          </EmptyState>
        </PageBody>
      </Page>
    )
  }

  /**
   * La demande n'est ni dans la tournée téléchargée, ni déjà commencée : l'écran s'arrête là.
   * Rien n'a été écrit — c'est tout l'intérêt : une saisie faite ici serait perdue au dépôt.
   */
  if (draft.data === null) {
    return (
      <Page>
        <PageHeader back={back} title="Contrôle inconnu" />
        <PageBody>
          <EmptyState
            icon={<ClipboardList size={18} aria-hidden />}
            title="Ce contrôle n'est pas dans la tournée de la tablette"
          >
            Rien n'a été créé, et rien ne serait transmis si vous saisissiez ici. Retéléchargez la
            tournée : si le contrôle vous est bien affecté, la fiche s'ouvrira d'elle-même.
          </EmptyState>
          <p>
            <Button onClick={redownloadTour} disabled={redownload === 'running'}>
              {redownload === 'running' ? 'Téléchargement…' : 'Retélécharger la tournée'}
            </Button>
          </p>
          {/* `role="status"` : le résultat arrive après le geste, un lecteur d'écran doit
              l'annoncer sans voler le focus. */}
          {redownload !== 'idle' && redownload !== 'running' && (
            <p role="status">{REDOWNLOAD_MESSAGE[redownload]}</p>
          )}
        </PageBody>
      </Page>
    )
  }

  if (draft.data === undefined) {
    // ⚠️ Volontairement sans `PageHeader` : ce sont quelques millisecondes de lecture
    // IndexedDB, et un second `<h1>` transitoire ferait annoncer un titre qui n'est pas
    // celui de la page à un lecteur d'écran — comme aux tests qui cherchent le titre.
    return (
      <Page>
        <PageBody>
          <EmptyState title="Ouverture de la fiche…">
            Le brouillon est relu dans la base de la tablette.
          </EmptyState>
        </PageBody>
      </Page>
    )
  }

  return (
    <OpenedInspection
      // ⚠️ `key` : changer de contrôle doit **remonter** l'éditeur, sinon la saisie en cours
      // resterait affichée sur la fiche suivante.
      key={inquiryId}
      context={context}
      inquiryId={inquiryId}
      inquiry={inquiry.data ?? null}
      openedDraft={draft.data}
      back={back}
      history={history}
    />
  )
}

/**
 * Ce que l'écran dit d'une écriture locale en échec **dont il ne peut pas nommer la cause** —
 * le quota plein est le cas réaliste, et « réessayez » y est la bonne conduite.
 */
const WRITE_ERROR_MESSAGE =
  "La dernière saisie n'a pas pu être enregistrée sur la tablette. Réessayez ; si cela se " +
  'répète, le stockage est peut-être plein — prévenez le bureau avant de continuer.'

/**
 * Il n'y a **plus de ligne** à cette clé. La fiche a quitté la base sous l'écran (éviction du
 * navigateur, base remise à neuf), et ce que l'agent lit n'est plus porté par rien.
 *
 * ⚠️ Le message ne dit pas « réessayez » : une seconde frappe irait au même vide. Il dit de
 * rouvrir la fiche pour repartir de ce qu'elle porte vraiment — et de **ne pas** ressaisir
 * ailleurs, une saisie rangée sur une autre fiche étant la seule perte irréversible d'ici.
 */
const MISSING_ROW_MESSAGE =
  "Cette saisie n'a pas pu être enregistrée : la fiche n’est plus dans la base de la tablette. " +
  'Fermez et rouvrez-la pour repartir de ce qu’elle porte vraiment, et prévenez le bureau — ne ' +
  'ressaisissez rien sur une autre fiche en attendant.'

/**
 * La fiche a été **transmise depuis un autre écran** de la tablette (la PWA ouverte deux fois
 * partage une base) : `updateDraft` refuse la saisie (`LockedDraftError`), et l'éditeur passe en
 * consultation de la fiche telle qu'elle est partie. Ce qui a été tapé ici après n'y est pas —
 * le dire, et dire à qui s'adresser, puisque la fiche ne se rouvre plus d'ici.
 */
const LOCKED_DRAFT_MESSAGE =
  "Cette saisie n'a pas été enregistrée : ce contrôle a déjà été transmis depuis un autre " +
  'écran de cette tablette. La fiche affichée est celle qui est partie — ce qui a été saisi ' +
  'ici après la transmission n’y figure pas. Prévenez le bureau s’il faut la compléter.'

/**
 * Safari en navigation privée range le texte mais refuse les images (photo, schéma, paraphe),
 * avec ce message et aucun code plus précis. Rien à réessayer : seule une fenêtre normale — ou
 * l'app installée, celle du terrain — garde les pièces.
 */
const PRIVATE_BROWSING_MESSAGE =
  "Cette image n'a pas pu être enregistrée : Safari ne garde pas les images en navigation " +
  'privée. Ouvrez l’application dans une fenêtre normale, ou depuis l’icône installée, puis ' +
  'recommencez.'

const PRIVATE_BROWSING_REFUSAL = /Error preparing Blob\/File data/

/**
 * Ce que l'écran dit d'une écriture locale en échec, **selon ce qui a échoué**.
 *
 * Trois échecs, trois conduites, et elles ne se ressemblent pas : devant une ligne absente on
 * rouvre la fiche, devant une fiche transmise d'un autre écran on prévient le bureau, devant une
 * tablette pleine on réessaie. Un message unique enverrait les deux premières chercher une panne
 * de stockage sur une tablette qui va très bien — c'est le bandeau lui-même qui mentirait, alors
 * qu'il existe pour faire remonter « la 40ᵉ photo n'apparaît pas ».
 *
 * ⚠️ **Jamais `failure.message` d'une erreur quelconque** : `MissingRowError` porte table et
 * clé, qui ne sont pas des mots d'agent, et une exception de Dexie parlerait anglais.
 */
/** Une photo ou une vidéo : l'appareil les nomme d'un compteur, jamais de ce qu'elles montrent. */
function isCameraFile(file: File): boolean {
  return file.type.startsWith('image/') || file.type.startsWith('video/')
}

function writeFailureMessage(failure: unknown): string {
  if (failure instanceof MissingRowError) return MISSING_ROW_MESSAGE
  if (failure instanceof LockedDraftError) return LOCKED_DRAFT_MESSAGE
  if (failure instanceof Error && PRIVATE_BROWSING_REFUSAL.test(failure.message)) {
    return PRIVATE_BROWSING_MESSAGE
  }

  return WRITE_ERROR_MESSAGE
}

/**
 * Ce que l'écran dit d'un référentiel qui n'a pas pu se mettre à jour. `unreachable` reste
 * muet : hors réseau, le cache est le fonctionnement prévu, pas un incident à annoncer.
 */
const VOCABULARY_WARNING: Record<PullPresentation, string | null> = {
  done: null,
  unreachable: null,
  refused:
    "Le référentiel d'anomalies n'a pas pu être mis à jour : le serveur a refusé l'accès avec ce " +
    'compte. Les anomalies proposées sont celles déjà sur la tablette — reconnectez-vous si la ' +
    'liste semble incomplète.',
  faulty:
    "Le référentiel d'anomalies n'a pas pu être mis à jour, et ce n'est pas une affaire de " +
    'réseau : prévenez le bureau. Les anomalies proposées sont celles déjà sur la tablette.',
}

interface OpenedInspectionProps {
  context: FieldContext
  inquiryId: string
  /**
   * La demande **telle que la tablette la garde** et non la vue du domaine : c'est `servedAt`,
   * que seule elle porte, qui distingue un contrôle transmis d'un contrôle renvoyé au terrain.
   */
  inquiry: CachedInquiry | null
  /** Le brouillon **tel qu'il a été relu** : l'état d'édition part de là. */
  openedDraft: InspectionDraft
  back: React.ReactNode
  /** Le chemin vers l'Historique, offert une fois l'envoi accepté (cf. `SubmitTab`). */
  history: React.ReactNode
}

/**
 * La fiche ouverte et sa saisie.
 *
 * Le brouillon vit en état React **et** dans Dexie : l'état rend la frappe instantanée, Dexie
 * la rend survivable. L'inverse — tout relire depuis Dexie à chaque touche — ferait revenir la
 * valeur précédente sous les doigts de l'agent le temps de l'aller-retour.
 *
 * ⚠️ L'écriture n'attend pas : elle part à chaque changement, sans anti-rebond. Un anti-rebond
 * ferait perdre la dernière saisie à toute fermeture d'onglet, et c'est justement celle qu'on
 * ne peut pas ressaisir.
 */
function OpenedInspection({
  context,
  inquiryId,
  inquiry,
  openedDraft,
  back,
  history,
}: OpenedInspectionProps) {
  const { shared, sync, accessToken, geolocation, requestSync } = context

  /**
   * Ce que l'organisation autorise, tel que la dernière tournée l'a redescendu (M35) — dans la
   * base partagée : c'est une règle de GHP, la même pour tous les binômes.
   */
  const rules = useQuery({
    queryKey: SERVER_RULES_QUERY_KEY,
    queryFn: () => readServerRules(shared),
  })
  const { auth } = inspectionEditorRoute.useRouteContext()
  const contactsCorrection = useContactsCorrection({ ...context, auth }, inquiryId)
  const data = useTeamData()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<InspectionDraft>(openedDraft)
  const [writeError, setWriteError] = useState<string | null>(null)
  // La fiche peut être transmise depuis un autre onglet (même base) : son verrou arrive par la
  // relecture de la base, jamais par cet état — et un verrou posé ailleurs l'emporte.
  useEffect(() => {
    setDraft((current) => followStoredLock(current, openedDraft))
  }, [openedDraft])

  /**
   * Une écriture locale part **sans qu'on l'attende** : la frappe de l'agent ne doit pas
   * dépendre d'un aller-retour IndexedDB.
   *
   * ⚠️ Son échec, lui, **s'affiche**, et il dit **lequel** (`writeFailureMessage`) : depuis que
   * l'accesseur lève au lieu de rendre `0` (D22 pt.4), un échec arrive ici avec sa cause, et
   * trois causes appellent trois conduites. Silencieux, il serait indistinguable d'un succès :
   * la 40ᵉ photo n'apparaît pas, l'agent recommence, et rien ne dit que la tablette est pleine.
   * Une écriture qui aboutit ensuite efface le bandeau — l'incident est derrière.
   */
  const inBackground = useCallback((write: Promise<unknown>): void => {
    write.then(
      () => setWriteError(null),
      (failure: unknown) => {
        // La trace garde l'erreur **entière** — table, clé, pile : c'est le seul endroit où la
        // cause exacte reste lisible, le bandeau n'en portant que ce qui est utile à l'agent.
        console.error('[terrain] Écriture locale impossible', failure)
        setWriteError(writeFailureMessage(failure))
      },
    )
  }, [])

  // ⚠️ La **seule** lecture de cet écran qui ne vienne pas de la base de l'équipe : le
  // référentiel d'anomalies n'est la saisie de personne, il est remplacé en entier à chaque
  // descente (M17, M18) et vit dans la base partagée. En donner un exemplaire par équipe
  // viderait ce sélecteur pour un agent hors réseau, le temps d'une redescente.
  const vocabulary = useQuery({
    queryKey: VOCABULARY_QUERY_KEY,
    queryFn: () => shared.vocabulary.toArray(),
  })

  const media = useQuery({
    queryKey: [...MEDIA_QUERY_KEY, inquiryId],
    // ⚠️ **Dans l'ordre de la prise de vue**, qui est celui de la clé primaire (une uuid v7, M7)
    // et non celui de l'index lu : `buildSubmission` persiste cet ordre dans l'enveloppe que le
    // bureau relit, et une séquence photo réordonnée se voit côté client.
    queryFn: () => data.media.where('inquiryId').equals(inquiryId).sortBy('id'),
  })

  const outbox = useQuery({
    queryKey: OUTBOX_QUERY_KEY,
    queryFn: () => data.outbox.toArray(),
  })

  /**
   * Le référentiel redescend à l'ouverture de la fiche (D22 pt.2). Hors réseau,
   * `pullVocabulary` rend `unavailable` : le sélecteur d'anomalies garde ce qu'il avait, en
   * silence — c'est le mode dégradé prévu (D4), la passe suivante suffira.
   *
   * Un **refus** ou une réponse hors contrat, eux, s'affichent : attendre le réseau n'y
   * changera rien, et un agent devant un sélecteur d'anomalies incomplet doit savoir qu'il
   * l'est — le même échec sur la tournée est expliqué, celui-ci ne peut pas rester muet.
   */
  const [vocabularyWarning, setVocabularyWarning] = useState<string | null>(null)

  useEffect(() => {
    // Pas `inBackground` : un référentiel qui ne descend pas n'est pas une saisie perdue,
    // le bandeau d'échec d'écriture raconterait une autre histoire que la vraie.
    // Le `401` remonte à la session comme partout ailleurs : ouvrir la fiche avec un jeton
    // mort doit déclencher la redescente en dégradé, pas attendre le planificateur.
    // Le référentiel n'appartient à aucune équipe : seul le jeton compte ici.
    pullVocabulary(shared, sync, accessToken(), (token) => auth.tokenRefused(token))
      .then((result) => {
        setVocabularyWarning(VOCABULARY_WARNING[pullPresentationOf(result)])
        if (result.outcome === 'updated') {
          return queryClient.invalidateQueries({ queryKey: VOCABULARY_QUERY_KEY })
        }
      })
      .catch((failure: unknown) => {
        console.error('[terrain] Descente du référentiel impossible', failure)
        setVocabularyWarning(VOCABULARY_WARNING.faulty)
      })
  }, [shared, sync, accessToken, auth, queryClient])

  /**
   * ⚠️ **L'ordre est délibéré, et il reste : on peint, puis on écrit.** L'inverse — attendre
   * IndexedDB avant d'afficher — ferait revenir la valeur précédente sous les doigts de l'agent
   * à chaque frappe. Et sur un échec, la saisie peinte **n'est pas reprise** : l'effacer serait
   * retirer de l'écran le seul exemplaire d'un relevé de terrain, exactement la panne que tout
   * ce module existe pour rendre impossible. Ce que l'échec change, c'est le bandeau — qui dit
   * désormais **laquelle** des trois causes (`writeFailureMessage`), donc quoi faire.
   *
   * Ce que cet ordre laisse ouvert, et qui est assumé : entre l'échec et la nouvelle tentative,
   * l'écran porte une saisie que Dexie n'a pas. Transmettre ne peut pas la figer en silence pour
   * autant — `enqueueSubmission` verrouille par une écriture **sur cette même ligne**, donc une
   * ligne absente le fait lever au lieu de lui donner une fiche à transmettre. Reste le seul cas où le verrou (minuscule) passerait là où le relevé (volumineux)
   * a saturé le quota : l'enveloppe partirait alors sans la dernière saisie. La sortie proposée
   * n'est pas de barrer le bouton — l'onglet « Transmettre » avertit, il ne bloque pas (M21) —
   * mais de confronter, au moment du verrou, la fiche relue à celle que l'écran porte.
   */
  /**
   * L'id du média `schematic`, tenu en ref **pour la même raison que celui du paraphe** : deux
   * appuis rapides sur « Télécharger l’image » lisaient tous deux le même instantané des
   * pièces — leur invalidation est asynchrone —, tiraient deux identifiants et écrivaient deux
   * images d'archive du même contrôle, dont une seule ressemble encore au relevé.
   */
  const schematicMediaIdRef = useRef<string | null>(null)

  /**
   * Retire l'image d'archive, si le contrôle en porte une. Une image déjà absente n'est pas une
   * erreur : le dessin est déjà comme voulu.
   */
  const discardSchematicImage = useCallback(() => {
    const existing =
      schematicMediaIdRef.current ??
      media.data?.find((medium) => medium.type === 'schematic')?.id ??
      null
    if (existing === null) return

    schematicMediaIdRef.current = null

    inBackground(
      data.media
        .delete(existing)
        .then(() => queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEY })),
    )
  }, [data, media.data, queryClient, inBackground])

  const applyPatch = useCallback(
    (patch: Partial<InspectionDraft>) => {
      const updatedAt = new Date().toISOString()
      setDraft((current) => ({ ...current, ...patch, updatedAt }))
      inBackground(updateDraft(data, inquiryId, { ...patch, updatedAt }))

      // « Valider la signature » (#352) : l'étape « Signé » part au bureau (M42), une fois — le
      // geste ne s'offre plus sur une signature verrouillée.
      if (patch.signatureLockedAt != null) {
        inBackground(recordInspectionStep(data, inquiryId, 'signed', patch.signatureLockedAt))
      }

      // ⚠️ Un plan redevenu vide **emporte son image d'archive**. L'agent qui exporte puis se
      // ravise et efface tout ne peut plus réexporter — le bouton se ferme sur un plan vide —
      // et aucun écran ne lui offre de retirer le JPEG périmé, qui partirait avec l'enveloppe
      // en montrant au bureau un schéma que le relevé ne porte plus.
      if (patch.survey !== undefined && (patch.survey.schematic?.nodes.length ?? 0) === 0) {
        discardSchematicImage()
      }
    },
    [data, inquiryId, inBackground, discardSchematicImage],
  )

  /**
   * Une photo prise. Son `id` est une **uuid v7 tirée par la tablette** (M7) : c'est elle qui
   * sert aussi de clé à l'objet distant, donc ce qui rend le `PUT` rejouable sans doublon.
   * L'horodatage est celui de la capture — il ne se reconstitue pas plus tard.
   *
   */
  const capture = useCallback(
    (file: File, caption: string | null) => {
      const medium: LocalMedium = {
        id: newUuidV7(),
        inquiryId,
        blob: file,
        type: 'view',
        takenAt: new Date().toISOString(),
        // Le nom de l'agent d'abord : c'est ce qu'il a écrit pour le bureau. Le nom du fichier
        // ne sert de repli qu'à un document — « IMG_4312.jpg » ou « VID_0042.mp4 » ne disent
        // rien à personne, et une photo sans nom doit rester signalée « à nommer » (#358).
        label: caption ?? (isCameraFile(file) || file.name === '' ? null : file.name),
        uploadedAt: null,
      }

      inBackground(
        data.media
          .put(medium)
          // ⚠️ Invalidation **explicite** : `watchLocalData` n'observe que la file et les
          // brouillons. Sans elle, la photo serait rangée sans que rien ne l'affiche.
          .then(() => queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEY })),
      )
    },
    [data, inquiryId, queryClient, inBackground],
  )

  const changeMediumLabel = useCallback(
    (mediumId: string, label: string | null) => {
      inBackground(
        updateRow(data.media, mediumId, { label }).then(() =>
          queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEY }),
        ),
      )
    },
    [data, queryClient, inBackground],
  )

  const changeMediumType = useCallback(
    (mediumId: string, type: AttachmentType) => {
      inBackground(
        updateRow(data.media, mediumId, { type }).then(() =>
          queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEY }),
        ),
      )
    },
    [data, queryClient, inBackground],
  )

  /**
   * Retirer une pièce jointe (#320) : la ligne part de Dexie, et avec elle tout ce qui la ferait
   * voyager — l'enveloppe se compose au verrou depuis les pièces restantes, et les octets ne
   * montent qu'après. Le compteur de « Mes contrôles » suit par la même invalidation.
   *
   * ⚠️ **Refusé sur une fiche verrouillée**, même si un écran l'offrait : ce qui est parti au
   * bureau référence cette pièce, et l'effacer ici ferait échouer l'envoi pour pièce disparue.
   */
  const removeMedium = useCallback(
    (mediumId: string) => {
      if (draft.lockedAt !== null) return

      inBackground(
        data.media
          .delete(mediumId)
          .then(() => queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEY })),
      )
    },
    [data, draft.lockedAt, queryClient, inBackground],
  )

  /**
   * L'image d'archive du schéma (M11), rangée comme média `schematic` — même chemin que les
   * photos, donc même téléversement direct (D5) et même rejeu idempotent.
   *
   * ⚠️ **Une seule par contrôle, réécrite.** L'identifiant est relu depuis les pièces déjà
   * rangées : un uuid neuf à chaque export empilerait une image par appui, dont une seule
   * ressemble encore au relevé — le bureau en verrait plusieurs, toutes créditées du même
   * contrôle, sans savoir laquelle fait foi. `media` est déjà chargé ici, et la ligne survit
   * au rechargement là où une ref ne survivrait pas.
   *
   * ⚠️ `uploadedAt: null` : un schéma réexporté après un premier envoi doit **repartir**. Garder
   * l'horodatage ferait sauter le téléversement, et l'archive garderait l'image d'avant.
   */
  const exportSchematic = useCallback(
    (image: Blob, rendered: NetworkSchematic, origin: 'button' | 'auto') => {
      /*
       * ⚠️ **Le fichier part dans les téléchargements, en plus d'être joint au contrôle** —
       * seulement sur un appui : le rendu automatique de l'éditeur remplirait sinon les
       * téléchargements de la tablette à chaque retouche.
       *
       * Sans cela le bouton n'avait pour tout effet visible qu'une ligne de gris clair au bas de
       * la colonne : le média `schematic` est écarté des pièces jointes (`isAttachment`), donc
       * l'image n'était **visible nulle part** — l'agent appuyait, ne voyait rien, et concluait
       * que le bouton ne marchait pas. Il marchait : 26 Ko de JPEG partaient dans Dexie sans que
       * personne ne puisse les regarder.
       *
       * Même geste que l'export du journal de synchro (`features/sync/routes/notifications.tsx`),
       * à l'identique et pour la même raison : c'est le seul moyen, sur une tablette, de mettre
       * un fichier entre les mains de l'agent.
       */
      if (origin === 'button') {
        const url = URL.createObjectURL(image)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = schematicFileName(inquiry)
        anchor.click()
        URL.revokeObjectURL(url)
      }

      const existing =
        schematicMediaIdRef.current ??
        media.data?.find((medium) => medium.type === 'schematic')?.id ??
        newUuidV7()
      schematicMediaIdRef.current = existing

      inBackground(
        data.media
          .put({
            id: existing,
            inquiryId,
            blob: image,
            type: 'schematic',
            takenAt: new Date().toISOString(),
            label: null,
            uploadedAt: null,
            // ⚠️ **Le dessin que l'éditeur a rendu, pas celui du brouillon relu.** Entre l'appui
            // et cette ligne, la rastérisation a pris un tour de boucle : un trait posé
            // entre-temps ferait créditer l'image d'un dessin qu'elle ne montre pas, et l'écran
            // tairait précisément l'écart qu'on veut dire.
            sourceFingerprint: schematicFingerprint(rendered),
          })
          .then(() => queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEY })),
      )
    },
    [data, inquiryId, media.data, inquiry, queryClient, inBackground],
  )

  /**
   * L'id du média `signature`, tenu en ref : un paraphe en trois traits émet trois fois, et
   * chaque émission doit **réécrire le même enregistrement** — trois lignes `media` seraient
   * trois téléversements, dont deux orphelins. La ref est synchrone, là où relire le brouillon
   * dans Dexie laisserait deux traits rapprochés se croiser et tirer deux ids.
   */
  const signatureMediaIdRef = useRef(openedDraft.signatureMediaId)

  /**
   * Signer : ranger le tracé comme média `signature` et dater l'acte **d'abord**, relever la
   * position **ensuite** (M20).
   *
   * ⚠️ **La géolocalisation ne bloque jamais** (M21) — et surtout, rien de l'acte signé ne
   * l'attend : l'invite de permission peut rester sans réponse au-delà de tout délai, et le
   * signataire est déjà reparti. Le paraphe et `signedAt` sont persistés immédiatement ;
   * `lat`/`lon` arrivent en second patch quand le GPS répond, ou jamais — avec un
   * avertissement, que la validation verra.
   *
   * ⚠️ **Le réglage se lit ici, au point d'appel**, et pas en enveloppant le port plus haut :
   * un agent qui coupe le géoréférencement doit voir l'effet là où il a lieu, et un port
   * silencieusement bridé ailleurs rendrait `null` sans que rien, dans ce fichier, ne dise
   * pourquoi. Lu à chaque signature plutôt que capté au montage : la fiche peut rester ouverte
   * pendant que le réglage change sur l'autre écran.
   */
  const sign = useCallback(
    (signature: Blob) => {
      inBackground(
        (async () => {
          const signatureMediaId = signatureMediaIdRef.current ?? newUuidV7()
          signatureMediaIdRef.current = signatureMediaId
          const signedAt = new Date().toISOString()

          await data.media.put({
            id: signatureMediaId,
            inquiryId,
            blob: signature,
            type: 'signature',
            takenAt: signedAt,
            label: null,
            uploadedAt: null,
          })
          await queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEY })
          applyPatch({ signatureMediaId, signedAt })

          const { autoGeolocation } = await readDevicePreferences(shared)
          if (!autoGeolocation) return

          const position = await geolocation.current()
          if (position !== null) applyPatch({ lat: position.lat, lon: position.lon })
        })(),
      )
    },
    [data, shared, inquiryId, geolocation, queryClient, applyPatch, inBackground],
  )

  /**
   * Effacer la signature, pour de vrai : le média rangé part avec les pixels de l'écran.
   *
   * ⚠️ **La référence n'est rendue que si les octets sont bien partis.** Une suppression qui
   * échoue — base illisible, quota — laisse l'enregistrement en place : lâcher la référence
   * quand même ferait tirer un identifiant **neuf** au paraphe suivant, et les octets d'origine
   * resteraient sur la tablette sans que rien ne les désigne, comptés par personne, hors
   * d'atteinte de toute purge. Sur une signature, pièce quasi-légale, c'est le pire des deux
   * mondes : ni retirée, ni référencée.
   *
   * Une pièce **absente**, elle, se supprime sans erreur : le nettoyage d'une référence morte
   * reste donc toujours possible.
   */
  const clearSignature = useCallback(() => {
    const signatureMediaId = signatureMediaIdRef.current
    // Lâchée tout de suite : un paraphe qui repart avant la fin de la suppression doit viser un
    // identifiant neuf, jamais celui qu'on est en train de retirer — sinon il recréerait la
    // ligne que la suppression efface juste après, et la fiche référencerait un vide.
    signatureMediaIdRef.current = null

    inBackground(
      (async () => {
        if (signatureMediaId !== null) {
          try {
            await data.media.delete(signatureMediaId)
          } catch (failure) {
            // Reprise de la référence, sauf si un paraphe a déjà repris la main entre-temps :
            // c'est lui qui fait loi, l'agent vient de signer.
            if (signatureMediaIdRef.current === null) {
              signatureMediaIdRef.current = signatureMediaId
            }
            throw failure
          }
          await queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEY })
        }
        applyPatch({ signatureMediaId: null, signedAt: null, lat: null, lon: null })
      })(),
    )
  }, [data, queryClient, applyPatch, inBackground])

  /**
   * Transmettre : mettre en file **et** verrouiller, dans la même transaction (#47). La
   * passe qui suit (`requestSync` : refresh → envoi → descentes) est le **chemin en ligne**,
   * celui de tous les jours ; hors réseau elle ne fait rien et la file repartira toute seule
   * au retour du réseau — le geste de l'agent a abouti dans les deux cas.
   */
  const submit = useCallback(() => {
    inBackground(
      (async () => {
        await enqueueSubmission(data, inquiryId, () => new Date(), vocabulary.data)

        const locked = await data.drafts.get(inquiryId)
        if (locked !== undefined) setDraft(locked)

        // L'état de synchro doit apparaître **avant** la première tentative, pas après :
        // c'est lui qui dit à l'agent que son geste a été pris. L'observateur `liveQuery`
        // couvre les écritures d'arrière-plan ; celle-ci, on la connaît, on l'annonce.
        await queryClient.invalidateQueries({ queryKey: OUTBOX_QUERY_KEY })
        await requestSync()
        await queryClient.invalidateQueries({ queryKey: OUTBOX_QUERY_KEY })
      })(),
    )
  }, [data, inquiryId, requestSync, queryClient, inBackground, vocabulary.data])

  /**
   * « Réessayer l'envoi » : remettre l'envoi en jeu (un échec définitif redevient rejouable,
   * une reprise programmée saute) puis demander une **passe complète** — refresh compris,
   * sans quoi une session au jeton mort rejouerait exactement le même refus.
   */
  const retrySend = useCallback(() => {
    inBackground(
      (async () => {
        await rearmSubmission(data, inquiryId)
        await queryClient.invalidateQueries({ queryKey: OUTBOX_QUERY_KEY })
        await requestSync()
        await queryClient.invalidateQueries({ queryKey: OUTBOX_QUERY_KEY })
      })(),
    )
  }, [data, inquiryId, requestSync, queryClient, inBackground])

  /**
   * Rouvrir une fiche refusée (`rejected`) ou en échec (`failed`, un relevé trop lourd — #246) :
   * le serveur n'a rien enregistré — sans cette sortie, la fiche serait verrouillée sur la
   * tablette *et* refusée par le serveur, et le relevé n'existerait plus nulle part d'accessible.
   */
  const reopen = useCallback(() => {
    inBackground(
      (async () => {
        const reopened = await retrySubmission(data, inquiryId)
        setDraft(reopened)
        await queryClient.invalidateQueries({ queryKey: DRAFTS_QUERY_KEY })
      })(),
    )
  }, [data, inquiryId, queryClient, inBackground])

  /**
   * Reprendre un contrôle que **le bureau a renvoyé** (#228) : la fiche se déverrouille sous une
   * clé d'envoi neuve, et la correction repart comme une seconde proposition (D4).
   *
   * Aucune passe de synchro n'est demandée ici, à la différence de « Réessayer l'envoi » : il n'y
   * a rien à pousser tant que l'agent n'a pas corrigé, et c'est sa transmission qui repassera par
   * la file.
   */
  const reopenAfterReturn = useCallback(() => {
    inBackground(
      (async () => {
        const reopened = await reopenReturned(data, inquiryId)
        setDraft(reopened)
        await queryClient.invalidateQueries({ queryKey: DRAFTS_QUERY_KEY })
      })(),
    )
  }, [data, inquiryId, queryClient, inBackground])

  const noticedAt = usePassageNoticedAt()
  const lastEntry = outboxEntryOf(outbox.data ?? [], inquiryId)
  const syncState: OutboxState | null = lastEntry?.state ?? null

  /**
   * L'état du contrôle dans la tournée, par la **même** règle que « Mes contrôles »
   * (`lib/list-filters.ts`) : la fiche et la ligne qui l'ouvre ne peuvent pas se contredire.
   */
  const state =
    inquiry === null
      ? null
      : tourStateOf({
          inquiry,
          sync: syncState,
          sentAt: lastEntry?.sentAt ?? null,
          passageNoticedAt: noticedAt.get(inquiryId) ?? null,
        })

  return (
    <InspectionEditorScreen
      inquiry={inquiry}
      state={state}
      draft={draft}
      vocabulary={vocabulary.data ?? []}
      media={media.data ?? []}
      sync={syncState}
      syncError={lastEntry?.lastError ?? null}
      syncAttempts={lastEntry?.attempts ?? 0}
      allowAttachmentsAfterSubmission={rules.data?.allowAttachmentsAfterSubmission}
      writeError={writeError}
      pullWarning={vocabularyWarning}
      back={back}
      history={history}
      onDraftChange={applyPatch}
      onCapture={capture}
      onMediumTypeChange={changeMediumType}
      onMediumLabelChange={changeMediumLabel}
      onMediumRemove={removeMedium}
      onSchematicExport={exportSchematic}
      onSign={sign}
      onSignatureClear={clearSignature}
      onSubmit={submit}
      onRetrySend={retrySend}
      onReopen={reopen}
      onReopenReturned={reopenAfterReturn}
      contacts={contactsCorrection}
    />
  )
}
